import React from 'react';
import { AppIcon, type IconName } from './icons';
import {
  getSemanticIcon,
  type SemanticConcept,
  SEMANTIC_ICON_MAP,
} from './icons/semanticIconMap';
import { Button } from './Button';
import { cn } from '@/lib/utils';

export interface EmptyStateAction {
  label: string;
  onClick: () => void;
  icon?: IconName;
  className?: string;
}

/** compact = inline/sidebar, section = panel/card, page = full list area */
export type EmptyStateSize = 'compact' | 'section' | 'page' | 'sm' | 'md' | 'lg';

export interface EmptyStateProps {
  /** Short headline — one clear job */
  title: string;
  /** One supporting sentence */
  description?: string;
  /** Semantic concept or explicit icon name (preferred) */
  icon?: SemanticConcept | IconName;
  /** @deprecated Use `icon` instead */
  illustrationType?: string;
  /** compact | section | page — sm/md/lg aliases kept for compatibility */
  size?: EmptyStateSize;
  /** plain = no chrome; surface = muted bordered panel */
  variant?: 'plain' | 'surface';
  /** Page states fill the available canvas; compact drawer/modal states stay in flow. */
  placement?: 'natural' | 'center';
  /** Primary CTA */
  action?: EmptyStateAction;
  /** Secondary text action */
  secondaryAction?: EmptyStateAction;
  className?: string;
}

/* ── Legacy illustrationType → icon ───────────────────── */
const LEGACY_ICON_MAP: Record<string, IconName> = {
  users: 'users',
  user: 'user',
  document: 'fileText',
  search: 'search',
  mail: 'mail',
  folder: 'folder',
  inbox: 'inbox',
  project: 'project',
  company: 'company',
  milestone: 'milestone',
};

function isSemanticConcept(value: string): value is SemanticConcept {
  return value in SEMANTIC_ICON_MAP;
}

function resolveIcon(icon?: SemanticConcept | IconName, illustrationType?: string): IconName {
  if (icon) {
    if (typeof icon === 'string' && isSemanticConcept(icon)) {
      return getSemanticIcon(icon);
    }
    return icon as IconName;
  }
  if (illustrationType) {
    return LEGACY_ICON_MAP[illustrationType] ?? 'inbox';
  }
  return 'inbox';
}

function normalizeSize(size: EmptyStateSize): 'compact' | 'section' | 'page' {
  switch (size) {
    case 'sm':
    case 'compact':
      return 'compact';
    case 'lg':
    case 'page':
      return 'page';
    case 'md':
    case 'section':
    default:
      return 'section';
  }
}

/* ── Size presets ─────────────────────────────────────── */
const SIZE = {
  compact: {
    wrapper: 'py-6 px-4',
    ring: 'w-10 h-10',
    icon: 'w-4 h-4',
    gap: 'mb-3',
    title: 'text-sm',
    desc: 'mt-1 text-xs max-w-xs',
    actions: 'mt-3 gap-2',
    btnSize: 'sm' as const,
  },
  section: {
    wrapper: 'py-10 px-6',
    ring: 'w-12 h-12',
    icon: 'w-5 h-5',
    gap: 'mb-4',
    title: 'text-base',
    desc: 'mt-1.5 text-sm max-w-sm',
    actions: 'mt-4 gap-2.5',
    btnSize: 'sm' as const,
  },
  page: {
    wrapper: 'py-16 px-8',
    ring: 'w-14 h-14',
    icon: 'w-6 h-6',
    gap: 'mb-5',
    title: 'text-lg',
    desc: 'mt-2 text-sm max-w-md',
    actions: 'mt-6 gap-3',
    btnSize: 'md' as const,
  },
} as const;

/**
 * Premium minimal empty-state primitive.
 *
 * One headline, one supporting line, semantic icon, optional primary/secondary CTAs.
 * Use semantic `icon` props (`project`, `company`, `milestone`, `document`, …).
 */
const EmptyState: React.FC<EmptyStateProps> = ({
  title,
  description,
  icon,
  illustrationType,
  size = 'section',
  variant = 'plain',
  placement,
  action,
  secondaryAction,
  className = '',
}) => {
  const normalizedSize = normalizeSize(size);
  const s = SIZE[normalizedSize];
  const resolvedPlacement = placement ?? (normalizedSize === 'page' ? 'center' : 'natural');
  const iconName = resolveIcon(icon, illustrationType);

  return (
    <div
      className={cn(
        'w-full flex flex-col items-center justify-center text-center',
        resolvedPlacement === 'center' &&
          'flex-1 self-stretch min-h-[clamp(20rem,52vh,36rem)]',
        s.wrapper,
        variant === 'surface' &&
          'rounded-lg border border-border-subtle/60 bg-surface-muted/30',
        className,
      )}
      role="status"
      aria-live="polite"
    >
      <div
        className={cn(
          'shrink-0 flex items-center justify-center rounded-full',
          'bg-surface-muted/70 ring-1 ring-border-subtle/70',
          s.gap,
          s.ring,
        )}
      >
        <AppIcon name={iconName} className={cn(s.icon, 'text-text-muted')} />
      </div>

      <p className={cn(s.title, 'font-medium leading-snug text-text-primary')}>
        {title}
      </p>

      {description && (
        <p className={cn(s.desc, 'text-text-muted leading-relaxed')}>
          {description}
        </p>
      )}

      {(action || secondaryAction) && (
        <div className={cn('flex flex-col items-center sm:flex-row sm:justify-center', s.actions)}>
          {action && (
            <Button
              variant="primary"
              size={s.btnSize}
              className={action.className}
              onClick={action.onClick}
              startIcon={
                action.icon ? (
                  <AppIcon name={action.icon} size="xxs" />
                ) : undefined
              }
            >
              {action.label}
            </Button>
          )}
          {secondaryAction && (
            <Button
              variant="link"
              size={s.btnSize}
              onClick={secondaryAction.onClick}
            >
              {secondaryAction.label}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default EmptyState;
